// components/CalendarModal/CalendarModal.jsx
import React, { useState, useEffect } from 'react';
import './CalendarModal.css';

const CalendarModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  serviceName, 
  servicePrice,
  availableDates: externalAvailableDates 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState(externalAvailableDates || []);

  // Сначала объявляем все функции
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getCorrectDayOfWeek = (date) => {
    let day = date.getDay();
    return day === 0 ? 6 : day - 1;
  };

  const getWeekDays = () => {
    return ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  };

  const getMonthName = (monthIndex) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return months[monthIndex];
  };

  const formatDate = (date) => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const dayOfWeek = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'][date.getDay()];
    return `${day}.${month}.${year} (${dayOfWeek})`;
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  const handleDateSelect = (date) => {
    if (date && date.available) {
      setSelectedDate(date);
    }
  };

  const handleConfirm = () => {
    if (selectedDate && selectedDate.available) {
      onConfirm(selectedDate);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setCurrentMonth(new Date());
    onClose();
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    const firstDayOffset = getCorrectDayOfWeek(firstDay);
    for (let i = 0; i < firstDayOffset; i++) {
      days.push(null);
    }
    
    const today = new Date();
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const isToday = date.toDateString() === today.toDateString();
      
      const isAvailableDate = availableDates.find(ad => 
        ad.date.getDate() === date.getDate() && 
        ad.date.getMonth() === date.getMonth() && 
        ad.date.getFullYear() === date.getFullYear()
      );
      
      const available = isAvailableDate ? isAvailableDate.available : true;
      const isWeekendDay = isWeekend(date);
      
      days.push({
        date,
        day: i,
        month: month,
        year: year,
        available: available,
        isWeekend: isWeekendDay,
        isToday: isToday,
        isSelected: selectedDate && 
          selectedDate.date.getDate() === date.getDate() && 
          selectedDate.date.getMonth() === date.getMonth() && 
          selectedDate.date.getFullYear() === date.getFullYear()
      });
    }
    
    return days;
  };

  // Инициализация доступных дат, если они не переданы извне
  useEffect(() => {
    if (!externalAvailableDates) {
      const today = new Date();
      const dates = [];
      
      dates.push({
        date: new Date(today),
        day: today.getDate(),
        month: today.getMonth(),
        year: today.getFullYear(),
        available: true,
        isWeekend: isWeekend(today),
        isToday: true
      });
      
      for (let i = 1; i <= 59; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        
        const isWeekendDay = isWeekend(date);
        const isAvailable = !(isWeekendDay && Math.random() < 0.5);
        
        dates.push({
          date,
          day: date.getDate(),
          month: date.getMonth(),
          year: date.getFullYear(),
          available: isAvailable,
          isWeekend: isWeekendDay,
          isToday: false
        });
      }
      
      setAvailableDates(dates);
    }
  }, [externalAvailableDates]);

  if (!isOpen) return null;

  return (
    <div className="calendar-modal-overlay" onClick={handleClose}>
      <div className="calendar-modal" onClick={(e) => e.stopPropagation()}>
        <div className="calendar-modal-header">
          <h3>
            {serviceName || 'Выберите дату записи'}
            {servicePrice && (
              <span className="service-price"> - {servicePrice}</span>
            )}
          </h3>
          <button className="modal-close-btn" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="selected-date-display">
          {selectedDate ? (
            <>
              <div className="selected-date-label">Выбранная дата:</div>
              <div className="selected-date-value">
                {formatDate(selectedDate.date)}
              </div>
            </>
          ) : (
            <div className="no-date-selected">
              Выберите дату для записи
            </div>
          )}
        </div>

        <div className="calendar-container">
          <div className="calendar-navigation">
            <button className="nav-btn prev-btn" onClick={goToPreviousMonth}>
              ←
            </button>
            <div className="current-month">
              {getMonthName(currentMonth.getMonth())} {currentMonth.getFullYear()}
            </div>
            <button className="nav-btn next-btn" onClick={goToNextMonth}>
              →
            </button>
          </div>

          <div className="week-days">
            {getWeekDays().map((day, index) => (
              <div key={index} className="week-day">{day}</div>
            ))}
          </div>

          <div className="calendar-days">
            {getDaysInMonth().map((day, index) => {
              if (!day) {
                return <div key={index} className="calendar-day empty"></div>;
              }

              return (
                <button
                  key={index}
                  className={`calendar-day ${day.available ? 'available' : 'unavailable'} ${day.isWeekend ? 'weekend' : ''} ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''}`}
                  onClick={() => handleDateSelect(day)}
                  disabled={!day.available}
                  title={!day.available ? 'Дата недоступна для записи' : 'Выбрать дату'}
                >
                  <div className="day-number">{day.day}</div>
                  {day.isToday && <div className="today-indicator">•</div>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="calendar-info">
          <div className="info-item">
            <div className="info-color available"></div>
            <span>Доступные даты</span>
          </div>
          <div className="info-item">
            <div className="info-color unavailable"></div>
            <span>Недоступные даты</span>
          </div>
          <div className="info-item">
            <div className="info-color today"></div>
            <span>Сегодня</span>
          </div>
        </div>

        <div className="calendar-actions">
          <button
            className="confirm-btn"
            onClick={handleConfirm}
            disabled={!selectedDate}
          >
            {selectedDate 
              ? `Записаться на ${formatDate(selectedDate.date)}` 
              : 'Записаться на выбранную дату'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarModal;